// L3-only org channel create/delete + membership admin for Firefly messenger.
import { useEffect, useState } from "react";
import type { MemberRow } from "./workplaceMessagesTypes";
import { WorkplaceChannelMemberPicker } from "./WorkplaceChannelMemberPicker";
import {
  getWorkplaceChatOrgChannelMembers,
  patchWorkplaceChatOrgChannelMembers,
} from "@/lib/workplaceChatClient";

export type WorkplaceOrgChannelAdminProps = {
  orgChannels: string[];
  members: MemberRow[];
  viewerUserId: string;
  onCreate: (input: {
    slug: string;
    display_name?: string;
    member_user_ids?: string[];
  }) => Promise<{ ok: boolean; error?: string }>;
  onDelete: (slug: string) => Promise<{ ok: boolean; error?: string }>;
  onChanged: () => void;
};

export function WorkplaceOrgChannelAdmin({
  orgChannels,
  members,
  viewerUserId,
  onCreate,
  onDelete,
  onChanged,
}: WorkplaceOrgChannelAdminProps) {
  const [slug, setSlug] = useState("");
  const [createMemberIds, setCreateMemberIds] = useState<string[]>(() =>
    viewerUserId ? [viewerUserId] : [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editMemberIds, setEditMemberIds] = useState<string[]>([]);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!viewerUserId) return;
    setCreateMemberIds((prev) => (prev.includes(viewerUserId) ? prev : [viewerUserId, ...prev]));
  }, [viewerUserId]);

  const deletable = orgChannels.filter((s) => s !== "general");

  async function openMembersEditor(channelSlug: string) {
    setEditingSlug(channelSlug);
    setEditLoading(true);
    setError("");
    const r = await getWorkplaceChatOrgChannelMembers(channelSlug);
    setEditLoading(false);
    if (!r.ok) {
      setError(r.error || "Could not load members");
      setEditMemberIds([viewerUserId].filter(Boolean));
      return;
    }
    setEditMemberIds(r.data.member_user_ids ?? []);
  }

  return (
    <div>
      <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200">
        Channels (Level 3) — include any org member (L1–L3)
      </p>
      <div className="mt-2 flex gap-2">
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="new-channel"
          autoComplete="off"
          className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
        />
        <button
          type="button"
          disabled={busy || !slug.trim() || createMemberIds.length === 0}
          onClick={() => {
            void (async () => {
              setBusy(true);
              setError("");
              const r = await onCreate({
                slug: slug.trim(),
                member_user_ids: createMemberIds,
              });
              setBusy(false);
              if (!r.ok) {
                setError(r.error || "Could not create channel");
                return;
              }
              setSlug("");
              setCreateMemberIds(viewerUserId ? [viewerUserId] : []);
              onChanged();
            })();
          }}
          className="rounded-md bg-teal-700 px-2 py-1 text-xs text-white disabled:opacity-50"
        >
          {busy ? "…" : "Create"}
        </button>
      </div>
      <WorkplaceChannelMemberPicker
        members={members}
        selectedIds={createMemberIds}
        onChange={setCreateMemberIds}
        lockedIds={viewerUserId ? [viewerUserId] : []}
        disabled={busy}
      />
      <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
        Only selected people will see and message in this channel.
      </p>

      {orgChannels.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {orgChannels.map((s) => (
            <li
              key={s}
              className="rounded-md border border-gray-100 px-2 py-1.5 dark:border-zinc-800"
            >
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="truncate font-medium text-gray-700 dark:text-gray-200">#{s}</span>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    disabled={busy || editLoading}
                    onClick={() => {
                      if (editingSlug === s) {
                        setEditingSlug(null);
                        return;
                      }
                      void openMembersEditor(s);
                    }}
                    className="rounded-md border border-teal-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-teal-900 hover:bg-teal-50 dark:border-teal-700 dark:bg-zinc-900 dark:text-teal-100"
                  >
                    {editingSlug === s ? "Close" : "Edit members"}
                  </button>
                  {s !== "general" ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        if (!window.confirm(`Delete #${s}? Members will lose this channel.`)) return;
                        void (async () => {
                          setBusy(true);
                          setError("");
                          const r = await onDelete(s);
                          setBusy(false);
                          if (!r.ok) {
                            setError(r.error || "Could not delete channel");
                            return;
                          }
                          if (editingSlug === s) setEditingSlug(null);
                          onChanged();
                        })();
                      }}
                      className="text-red-700 hover:underline dark:text-red-300"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
              {editingSlug === s ? (
                <div className="mt-1">
                  {editLoading ? (
                    <p className="text-[10px] text-gray-500">Loading members…</p>
                  ) : (
                    <>
                      <WorkplaceChannelMemberPicker
                        members={members}
                        selectedIds={editMemberIds}
                        onChange={setEditMemberIds}
                        disabled={busy}
                      />
                      <button
                        type="button"
                        disabled={busy || editMemberIds.length === 0}
                        onClick={() => {
                          void (async () => {
                            setBusy(true);
                            setError("");
                            const r = await patchWorkplaceChatOrgChannelMembers({
                              slug: s,
                              member_user_ids: editMemberIds,
                            });
                            setBusy(false);
                            if (!r.ok) {
                              setError(r.error || "Could not update members");
                              return;
                            }
                            setEditMemberIds(r.data.member_user_ids ?? editMemberIds);
                            onChanged();
                          })();
                        }}
                        className="mt-1 rounded-md bg-teal-700 px-2 py-1 text-[11px] text-white disabled:opacity-50"
                      >
                        Save members
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {deletable.length === 0 && orgChannels.includes("general") ? (
        <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
          Only custom channels can be deleted (#general is protected).
        </p>
      ) : null}
      {error ? <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">{error}</p> : null}
    </div>
  );
}
